import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useGetInvoiceQuery,
  useUpdateInvoiceMutation
} from '../../../../../services/endpoints/finance/invoice';
import { getCurrentUser } from '../../../../../services/helper';
import { dateToUTC, formatDate } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { InvoiceForm } from './InvoiceForm';
import { userCanAdmin_Invoices } from './InvoicesView';

const steps = [{ name: 'Details', form: InvoiceForm }];

export interface FormValues {
  id?: number;
  name: string;
  po_number?: string;
  issue_on: string;
  due_on: string;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditInvoiceProps {
  editing?: boolean;
}

export const AddEditInvoice: React.FC<AddEditInvoiceProps> = ({ editing }) => {
  const params = useParams();
  const currentUser = getCurrentUser();
  const [currentStep, setCurrentStep] = useState(0);
  const canAccessInvoices = userCanAdmin_Invoices(currentUser);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [updateInvoice] = useUpdateInvoiceMutation();
  const { data, error, isLoading } = useGetInvoiceQuery(Number(params.id!), {
    skip: !editing || !params.id || !canAccessInvoices
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    po_number: '',
    issue_on: formatDate(new Date(), 'yyyy-MM-dd'),
    due_on: ''
  });

  const validationSchemas = [
    Yup.object({
      name: Yup.string().required('Name is required'),
      po_number: Yup.string()
        .transform((value) => (!value || value === '' ? null : value))
        .nullable()
        .optional(),
      issue_on: Yup.string().required('Issued date is required'),
      due_on: Yup.string().required('Due date is required')
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading invoice', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.invoice) {
      const { invoice } = data;
      setInitialValues({
        id: invoice.id,
        name: invoice.name,
        po_number: invoice.po_number,
        issue_on: invoice.issue_on ?? formatDate(new Date(), 'yyyy-MM-dd'),
        due_on: invoice.due_on ?? ''
      });
    }
  }, [data]);

  const handleAddInvoice = async (formValue: FormValues) => {
    try {
      if (editing) {
        if (!canAccessInvoices) throw Error('You do not have permission to do this');

        const res = await updateInvoice({
          ...formValue,
          issue_on: dateToUTC(new Date(formValue.issue_on), 'MM/dd/yyyy'),
          due_on: dateToUTC(new Date(formValue.due_on), 'MM/dd/yyyy')
        }).unwrap();
        if (res && res.invoice) {
          addToast(`Invoice updated succesfully`, 'success');
          navigate(`/admin/finance/invoices/${data?.invoice.id}`);
        }
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        name: true,
        po_number: true,
        issue_on: true,
        due_on: true
      });
    }
  };

  if (!canAccessInvoices) {
    return <Forbidden />
  }

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to invoice${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/finance/invoices/${data?.invoice.id}` : '/admin/finance/invoices'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Invoice
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleAddInvoice}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
